import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardBody, Button, Skeleton } from '@heroui/react';
import { usePublishedGuideVersion } from '@/hooks/useGuides';
import { useLanguage } from '@/contexts/LanguageContext';
import GuideViewer from '@/components/guides/GuideViewer';
import { TransformedGuideData } from '@/types';

const GuideViewerPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentLanguage } = useLanguage();

  const { data: versionResponse, isLoading, error } = usePublishedGuideVersion(id || '');

  // Transform data to the format GuideViewer expects
  const transformedData = useMemo<TransformedGuideData | null>(() => {
    if (!versionResponse?.data) return null;

    const guideData = versionResponse.data as any;

    // Get current language translation or fallback
    let currentTranslation = guideData.translations?.find((t: any) => t.language === currentLanguage);
    if (!currentTranslation && guideData.translations?.length > 0) {
      currentTranslation = guideData.translations[0];
    }

    return {
      id: guideData.id,
      title: currentTranslation?.title || 'Untitled Guide',
      description: currentTranslation?.description,
      difficulty: guideData.difficulty,
      estimatedTime: guideData.estimatedTime,
      steps: guideData.steps || [],
      translations: guideData.translations || [],
    };
  }, [versionResponse, currentLanguage]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-12 w-3/4 mb-4 rounded-lg" />
          <Skeleton className="h-6 w-1/2 mb-8 rounded-lg" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !transformedData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardBody className="text-center space-y-4">
            <h2 className="text-2xl font-bold text-danger">Error Loading Guide</h2>
            <p className="text-foreground-500">
              {error ? 'An error occurred while loading the guide.' : 'Guide not found or no translation available.'}
            </p>
            <Button color="primary" onPress={() => navigate('/guides')}>
              Back to Guides
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return <GuideViewer guideData={transformedData} guideId={id || ''} />;
};

export default GuideViewerPage;
